import React from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { FormattedMessage, useIntl } from 'react-intl'

import s from '../styles.module.scss'

import { LoginModalTypeEnum } from '../login.enum'
import { LoginOptionsProps } from '../login.types'
import LoginModalContainer from '../login-modal-container/login-modal-container'
import { SsoLogin } from '../sso-login/SsoLogin'
import { LOGIN_SITE } from '../../login/login.enum'
import LoginSiteTab from '../../login/login-site-tab/login-site-tab'
import { Button, BUTTON_COLOR } from '../../../../components/atoms'
import { InputText } from '../../../../components/molecules'
import { breakpoints, useMediaQuery } from '../../../../lib/hooks'
import { formatError, formatGqlError } from '../../../../utils/format-error'
import regex from '../../../../utils/regex'

export interface InputsLogin {
  email: string
  password: string
}

const LoginForm = ({
  onLogin,
  isLoading,
  modalType: { options },
  setModalType,
  ssoOptions,
}: LoginOptionsProps) => {
  const {
    control,
    formState: { isValid },
    handleSubmit,
    setError,
  } = useForm<InputsLogin>({
    defaultValues: { email: options?.defaultValues?.email || '', password: '' },
    mode: 'onChange',
  })

  const intl = useIntl()
  const isMobile = useMediaQuery(breakpoints.newMobile)

  const onSubmit: SubmitHandler<InputsLogin> = async ({ email, password }) => {
    try {
      await onLogin({ email, password })
    } catch (e) {
      const gqlError = formatGqlError(e)
      const defaultError = formatError(e)

      const errorMessage = gqlError?.message || defaultError?.message

      if (errorMessage.includes('ERROR_WITH_CREDENTIALS')) {
        setError('password', {
          type: 'string',
          message: intl.formatMessage({
            id: 'ui.LoginForm.errorEmailIncorrect',
            defaultMessage: 'Le mot de passe saisi est incorrect',
          }),
        })
      } else if (errorMessage.includes('EMAIL_NOT_FOUND')) {
        setError('email', {
          type: 'string',
          message: intl.formatMessage({
            id: 'ui.LoginForm.errorEmailNotFound',
            defaultMessage: "L'adresse mail saisie est introuvable",
          }),
        })
      } else {
        setError('password', {
          type: 'string',
          message: intl.formatMessage({
            id: 'ui.LoginForm.errorPasswordComeBackLater',
            defaultMessage:
              'Une erreur est survenue. Veuillez réessayer ultérieurement.',
          }),
        })
        setError('email', {
          type: 'string',
          message: intl.formatMessage({
            id: 'ui.LoginForm.errorEmailComeBackLater',
            defaultMessage:
              'Une erreur est survenue. Veuillez réessayer ultérieurement.',
          }),
        })
      }
    }
  }

  const canCloseOnClickOutside = options?.canCloseOnClickOutside

  const closeModal = () => {
    if (canCloseOnClickOutside === false) {
      return
    } else {
      setModalType({ type: LoginModalTypeEnum.Close })
    }
  }

  return (
    <LoginModalContainer
      closeModal={closeModal}
      title='Se connecter'
      customHeader={
        <div className={s.customHeader}>
          <LoginSiteTab
            loginSiteNavigator={{
              activeSite: LOGIN_SITE.USER,
              onChangeActiveSite: (arg) => {
                if (arg === LOGIN_SITE.OWNER) {
                  process.env.NEXT_OWNER_APP_URL &&
                    window.open(process.env.NEXT_OWNER_APP_URL, '_self')
                }
              },
            }}
          />
          <div>
            <div className={s.title}>
              <FormattedMessage
                id='ui.loginForm.signUpTitle'
                defaultMessage="Se connecter à l'espace utilisateur"
              />
            </div>
          </div>
        </div>
      }
      mobileHeader={
        <LoginSiteTab
          loginSiteNavigator={{
            activeSite: LOGIN_SITE.USER,
            onChangeActiveSite: (arg) => {
              if (arg === LOGIN_SITE.OWNER) {
                process.env.NEXT_OWNER_APP_URL &&
                  window.open(process.env.NEXT_OWNER_APP_URL, '_self')
              }
            },
          }}
          style={{ marginBottom: 0 }}
        />
      }
      browserFooter={{
        onClick: () =>
          setModalType({ type: LoginModalTypeEnum.Subscribe, options }),
        label: (
          <FormattedMessage
            id='ui.loginForm.label.subscribe'
            defaultMessage="Vous n'avez pas encore de compte ? Inscrivez-vous."
          />
        ),
        id: 'no-account-login',
        'data-testid': 'login-modal-sign-up-button',
      }}
      style={
        !isMobile ? { maxHeight: 1000, width: 'auto', height: 'auto' } : {}
      }
      styleContent={{ height: '100%' }}
      styleChildrenModal={isMobile ? { paddingTop: 10 } : {}}
      styleContainer={{
        padding: 20,
        height: '100%',
      }}
    >
      <div
        className={s.formContainer}
        style={{ paddingTop: 30, paddingLeft: 12, paddingRight: 12 }}
      >
        <div>
          <p className={s.description}>
            <FormattedMessage
              id='ui.loginForm.signUpDescription'
              defaultMessage='Vous êtes déjà inscrit sur Naboo ? Connectez-vous pour réserver'
            />
          </p>
          <form className={s.form} style={{ gap: 0 }}>
            <InputText
              control={control}
              name='email'
              label={
                <FormattedMessage
                  id='ui.loginForm.placeHolder.emailLabel'
                  defaultMessage='E-mail professionnel'
                />
              }
              rules={{
                required: intl.formatMessage({
                  id: 'ui.LoginForm.emailRequired',
                  defaultMessage: 'Une adresse e-mail est requise.',
                }),
                pattern: {
                  value: regex.email,
                  message: intl.formatMessage({
                    id: 'ui.LoginForm.wrongEmailFormat',
                    defaultMessage: "Respecter le bon format d'e-mail",
                  }),
                },
              }}
              inputProps={{
                type: 'text',
                'aria-label': intl.formatMessage({
                  id: 'ui.LoginForm.enterEmail',
                  defaultMessage: 'Entrez votre e-mail',
                }),
                'aria-required': 'true',
              }}
              placeholder={intl.formatMessage({
                id: 'ui.loginForm.placeHolder.email',
                defaultMessage: 'Entrez votre e-mail',
              })}
              dataTestId='email-input'
            />

            <InputText
              control={control}
              name='password'
              label={
                <FormattedMessage
                  id='ui.loginForm.placeHolder.passwordLabel'
                  defaultMessage='Mot de passe'
                />
              }
              rules={{
                required: intl.formatMessage({
                  id: 'ui.LoginForm.passwordRequired',
                  defaultMessage: 'Un mot de passe est requis.',
                }),
              }}
              inputProps={{
                type: 'password',
                'aria-label': intl.formatMessage({
                  id: 'ui.LoginForm.enterPassword',
                  defaultMessage: 'entrez votre mot de passe',
                }),
                'aria-required': 'true',
              }}
              placeholder={intl.formatMessage({
                id: 'ui.loginForm.placeHolder.password',
                defaultMessage: 'Entrez votre mot de passe',
              })}
              dataTestId='password-input'
            />

            <div className={s.forgetPassword}>
              <span
                className={s.underline}
                onClick={() =>
                  setModalType({
                    type: LoginModalTypeEnum.ForgotPassword,
                    options,
                  })
                }
                id='forgot-password'
                data-testid='forgot-password-button'
              >
                <FormattedMessage
                  id='ui.loginForm.forgottenPassword'
                  defaultMessage='Mot de passe oublié ?'
                />
              </span>
            </div>

            <Button
              className={s.loginBtn}
              loading={isLoading}
              onClick={handleSubmit(onSubmit)}
              disabled={!isValid}
              fullWidth={true}
              id='login-success'
              data-testid='login-submit-button'
            >
              <FormattedMessage
                id='ui.loginForm.login'
                defaultMessage='Me connecter'
              />
            </Button>

            <SsoLogin {...ssoOptions} />
          </form>
          <div className={s.footer}>
            <FormattedMessage
              id='ui.loginForm.footer.noAccount'
              defaultMessage="Vous n'avez pas de compte ?"
            />
            {'\u00A0'}
            <Button
              onClick={() =>
                setModalType({ type: LoginModalTypeEnum.Subscribe })
              }
              color={BUTTON_COLOR.LINK}
            >
              <FormattedMessage
                id='ui.loginForm.footer.subscribeHere'
                defaultMessage='Inscrivez-vous ici'
              />
            </Button>
          </div>
        </div>
      </div>
    </LoginModalContainer>
  )
}

export default LoginForm
